import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { findCurrentLyricIndex } from '../../utils/lrcParser';
import {
    TurntableBase,
    TurntablePlatter,
    RecordImage,
    RecordGrooves
} from './Turntable';
import EQControls from './EQControls';
import { VolumeSection, VolumeSlider, VolumeLines, VolumeLine, VolumeTrack, VolumeHandle } from './VolumeSlider';
import { BPMSliderContainer, BPMValue, BPMMarkers, BPMMarker, BPMSlider, BPMSliderTrack, BPMSliderHandle } from './BPMSlider';
import { CueButton, CueKeysContainer, CueKey } from './CueButton';
import PlayPauseButton from './PlayPauseButton';
import { ProgressBar, ProgressFill, TimeDisplay } from './ProgressBar';
import {
    initializeAudio,
    ensureAudioNodes as ensureAudioNodesUtil,
    updateEQGain,
    updateVolume
} from '../../utils/audioUtils';

const BASE_WIDTH = {
    large: 500,
    medium: 380,
    small: 250,
};
const BASE_HEIGHT = {
    large: 500,
    medium: 380,
    small: 280,
};  

const PLATTER_SIZES = {
    large: 300,
    medium: 240,
    small: 180,
};

// 组件位置和大小配置
const COMPONENT_LAYOUTS = {
    large: {
        platter: { top:  50, left: 100, width: 300, height: 300 }, // 默认居中，只设置大小
        controlsSection: { top: 120, right: 20, width: 'auto', height: 'auto' },
        bpmSliderContainer: { right: 80, bottom: 30, width: 10, height: 150 },
        volumeSliderContainer: { bottom: 80, right: 20, width: 'auto', height: 90 },
        cueButton: { left: 20, bottom: 80, width: 50, height: 50 },
        cueKeys: { bottom: 20, left: '50%', transform: 'translateX(-50%)', width: 50, height: 50 },
        playPause: { bottom: 20, left: 20, width: 50, height: 50 },
        progressSection: { bottom: 80, left: '50%', transform: 'translateX(-50%)', width: '50%', maxWidth: 300 },
        eqControlsContainer: { top: -50, right: -25, width: 80, height: 200 },
        eqKnob: { knobSize: 30, scaleSize: 50, tickWidth: 1.5, tickHeight: 5, borderWidth: 2 },
        centerHole: { size: 30, dotSize: 12 }, // 中间洞的大小和中心点大小
    },
    medium: {
        platter: { top: 30, left: 70, width: 240, height: 240 },
        controlsSection: { top: 100, right: 15, width: 'auto', height: 'auto' },
        bpmSliderContainer: { right: 60, bottom: 25, width: 8, height: 120 },
        volumeSliderContainer: { bottom: 40, right: 10, width: 'auto', height: 90 },
        cueButton: { left: 15, bottom: 70, width: 45, height: 45 },
        cueKeys: { bottom: 15, left: '50%', transform: 'translateX(-50%)', width: 35, height: 35 },
        playPause: { bottom: 15, left: 15, width: 45, height: 45 },
        progressSection: { bottom: 70, left: '50%', transform: 'translateX(-50%)', width: '65%', maxWidth: 250 },
        eqControlsContainer: { top: -50, right: -30, width: 80, height: 160 },
        eqKnob: { knobSize: 28, scaleSize: 48, tickWidth: 1.5, tickHeight: 5, borderWidth: 2 },
        centerHole: { size: 20, dotSize: 10 },
    },
    small: {
        platter: { top: 20, left: 20, width: 160, height: 160 },
        controlsSection: { top: 80, right: 10, width: 'auto', height: 'auto' },
        bpmSliderContainer: { right: 80, bottom: 40, width: 6, height: 80 },
        volumeSliderContainer: { bottom: 40, right: 25, width: 'auto', height: 70 },
        cueKeys: { left: 110, bottom: 10, transform: 'translateX(-50%)', width: 23, height: 23 },
        cueButton: { left: 10, bottom: 50, width: 30, height: 30 },
        playPause: { bottom: 10, left: 10, width: 30, height: 30 },
        progressSection: { bottom: 60, left: '20%', width: '30%', maxWidth: 200 },
        eqControlsContainer: { top: -50, right: -10, width: 80, height: 80 },
        eqKnob: { knobSize: 20, scaleSize: 35, tickWidth: 1, tickHeight: 4, borderWidth: 1.5 },
        centerHole: { size: 10, dotSize: 8 },
    },
};

export const TurntableContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 40px 0;
    perspective: 1000px;
`;

export const TurntablePlatterContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

export const EQControlsContainer = styled.div`
    position: absolute;
    ${props => {
        const layout = props.layout || {};
        let styles = '';
        if (layout.top !== undefined) styles += `top: ${typeof layout.top === 'string' ? layout.top : `${layout.top}px`}; `;
        if (layout.right !== undefined) styles += `right: ${typeof layout.right === 'string' ? layout.right : `${layout.right}px`}; `;
        if (layout.bottom !== undefined) styles += `bottom: ${typeof layout.bottom === 'string' ? layout.bottom : `${layout.bottom}px`}; `;
        if (layout.left !== undefined) styles += `left: ${typeof layout.left === 'string' ? layout.left : `${layout.left}px`}; `;
        if (layout.width !== undefined) styles += `width: ${typeof layout.width === 'string' ? layout.width : `${layout.width}px`}; `;
        if (layout.height !== undefined) {
            const heightValue = typeof layout.height === 'string' ? layout.height : `${layout.height}px`;
            styles += `height: ${heightValue}; `;
        }
        if (layout.transform) styles += `transform: ${layout.transform}; `;
        return styles;
    }}
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: ${props => {
        const layout = props.layout || {};
        if (layout.height) {
            // 根据容器高度动态调整 gap，让子元素能够适应
            const height = typeof layout.height === 'string' ? parseInt(layout.height) : layout.height;
            // 假设每个 knob 大约 30-50px，label 大约 15px，总共约 45-65px
            // 如果有 3 个元素，总高度约 135-195px
            // 如果容器高度较小，减小 gap
            if (height < 100) return '4px';
            if (height < 150) return '8px';
            return '15px';
        }
        return '15px';
    }};
    z-index: 10;
`;

const ControlsSection = styled.div`
    position: absolute;
    ${props => {
        const layout = props.layout || {};
        let styles = '';
        if (layout.top !== undefined) styles += `top: ${typeof layout.top === 'string' ? layout.top : `${layout.top}px`}; `;
        if (layout.right !== undefined) styles += `right: ${typeof layout.right === 'string' ? layout.right : `${layout.right}px`}; `;
        if (layout.bottom !== undefined) styles += `bottom: ${typeof layout.bottom === 'string' ? layout.bottom : `${layout.bottom}px`}; `;
        if (layout.left !== undefined) styles += `left: ${typeof layout.left === 'string' ? layout.left : `${layout.left}px`}; `;
        if (layout.width !== undefined) styles += `width: ${typeof layout.width === 'string' ? layout.width : `${layout.width}px`}; `;
        if (layout.height !== undefined) styles += `height: ${typeof layout.height === 'string' ? layout.height : `${layout.height}px`}; `;
        if (layout.transform) styles += `transform: ${layout.transform}; `;
        return styles;
    }}
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    z-index: 10;
`;

const CenterSpindle = styled.div`
    position: absolute;
    width: ${props => props.size || 50}px;
    height: ${props => props.size || 50}px;
    background: radial-gradient(circle, #0a0a0a 0%, #000000 100%);
    border-radius: 50%;
    box-shadow: 
        inset 0 0 20px rgba(0, 0, 0, 0.9),
        0 2px 5px rgba(0, 0, 0, 0.5);
    z-index: 3;
    border: 2px solid rgba(244, 212, 163, 0.1);
`;

const CenterDot = styled.div`
    position: absolute;
    width: ${props => props.size || 12}px;
    height: ${props => props.size || 12}px;
    background: #000000;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 4;
`;

const flyOut = keyframes`
    0% {
        opacity: 0;
        transform: translate(0, 0) scale(0.8) rotate(0deg);
    }
    20% {
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: translate(var(--tx), var(--ty)) scale(1.2) rotate(var(--rotate));
    }
`;

const LyricBubble = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 8px 16px;
    background: rgba(244, 212, 163, 0.95);
    color: #1a1a3e;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
    pointer-events: none;
    z-index: 1000;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    animation: ${flyOut} 2s ease-out forwards;
    --tx: ${props => props.tx || '0px'};
    --ty: ${props => props.ty || '-100px'};
    --rotate: ${props => props.rotate || '0deg'};

    @media (max-width: 768px) {
        font-size: 0.75rem;
        padding: 6px 12px;
    }
`;


export const ProgressSection = styled.div`
    position: absolute;
    ${props => {
        const layout = props.layout || {};
        let styles = '';
        if (layout.top !== undefined) styles += `top: ${typeof layout.top === 'string' ? layout.top : `${layout.top}px`}; `;
        if (layout.right !== undefined) styles += `right: ${typeof layout.right === 'string' ? layout.right : `${layout.right}px`}; `;
        if (layout.bottom !== undefined) styles += `bottom: ${typeof layout.bottom === 'string' ? layout.bottom : `${layout.bottom}px`}; `;
        if (layout.left !== undefined) styles += `left: ${typeof layout.left === 'string' ? layout.left : `${layout.left}px`}; `;
        if (layout.width !== undefined) styles += `width: ${typeof layout.width === 'string' ? layout.width : `${layout.width}px`}; `;
        if (layout.height !== undefined) styles += `height: ${typeof layout.height === 'string' ? layout.height : `${layout.height}px`}; `;
        if (layout.transform) styles += `transform: ${layout.transform}; `;
        if (layout.maxWidth !== undefined) styles += `max-width: ${typeof layout.maxWidth === 'string' ? layout.maxWidth : `${layout.maxWidth}px`}; `;
        return styles;
    }}
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    z-index: 10;
`;

// 调试面板按钮
export const DebugPanelButton = styled.button`
    position: fixed;
    top: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    font-size: 18px;
    z-index: 10000;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
    
    &:hover {
        background: rgba(0, 0, 0, 0.9);
        border-color: rgba(255, 255, 255, 0.4);
        transform: scale(1.05);
    }
    
    &:active {
        transform: scale(0.95);
    }
    
    @media (max-width: 768px) {
        top: auto;
        bottom: 10px;
        right: 10px;
    }
`;

// 调试面板
export const DebugPanel = styled.div`
    position: fixed;
    top: 60px;
    right: 10px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: ${props => props.isOpen ? '12px 16px' : '0 16px'};
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    z-index: 10000;
    min-width: 200px;
    line-height: 1.6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-height: ${props => props.isOpen ? '500px' : '0'};
    overflow: hidden;
    opacity: ${props => props.isOpen ? '1' : '0'};
    transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease;
    transform-origin: top;
    
    @media (max-width: 768px) {
        top: calc(100vh - 550px);
        bottom: auto;
        right: 10px;
        left: 10px;
        min-width: auto;
        max-width: calc(100vw - 20px);
        max-height: ${props => props.isOpen ? 'calc(100vh - 60px)' : '0'};
    }
`;

const DJController = ({
    audioPath,
    baseBPM = 95,
    bpmAdjustmentPercentage = 0.07,
    onTimeUpdate,
    onBPMUpdate,
    onPlayingStateUpdate,
    onScrollToSection,
    onCueClick,
    lyrics = [],
    lrcData = [], // [{time: number, text: string}] — parsed LRC for time-based lyric lookup during scratch
    size = 'large' // 'large' | 'medium' | 'small'
}) => {
    const { t } = useTranslation();

    // 常量
    const BASE_BPM = baseBPM;
    const BPM_ADJUSTMENT_PERCENTAGE = bpmAdjustmentPercentage;

    // 状态管理
    const [isPlaying, setIsPlaying] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const [bpmValue, setBpmValue] = useState(50);
    const [cueActive, setCueActive] = useState(false);
    const [eqValues, setEqValues] = useState({ high: 50, mid: 50, low: 50 });
    const lastMouseAngleRef = useRef(0); // 跟踪上一次鼠标角度，用于连续旋转
    const rotationRef = useRef(0); // 跟踪当前的旋转角度，避免闭包问题
    const cumulativeAngleRef = useRef(0); // 跟踪累积的鼠标角度变化（用于连续旋转）
    const centerPointRef = useRef({ x: 0, y: 0 }); // 保存转盘中心点坐标
    const lastDragUpdateTimeRef = useRef(0); // 拖曳时的时间更新节流
    const [isDraggingBPM, setIsDraggingBPM] = useState(false);
    const [eqRotation, setEqRotation] = useState({ high: 0, mid: 0, low: 0 });
    const [isDraggingEQ, setIsDraggingEQ] = useState({ high: false, mid: false, low: false });
    const [eqStartY, setEqStartY] = useState({ high: null, mid: null, low: null });
    const [eqStartX, setEqStartX] = useState({ high: null, mid: null, low: null });
    const [eqStartRotation, setEqStartRotation] = useState({ high: 0, mid: 0, low: 0 });
    const [eqHasMoved, setEqHasMoved] = useState({ high: false, mid: false, low: false });
    const [volume, setVolume] = useState(100);
    const [isDraggingVolume, setIsDraggingVolume] = useState(false);
    const [isDraggingProgress, setIsDraggingProgress] = useState(false);
    const [lyricBubbles, setLyricBubbles] = useState([]);
    
    // 拖曳调试信息
    const [dragDebugInfo, setDragDebugInfo] = useState({
        centerX: 0,
        centerY: 0,
        mouseX: 0,
        mouseY: 0,
        currentAngle: 0,
        lastMouseAngle: 0,
        cumulativeAngle: 0,
        angleDelta: 0
    });

    // Refs
    const startTimeRef = useRef(0);
    const audioContextRef = useRef(null);
    const audioBufferRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const highFilterRef = useRef(null);
    const midFilterRef = useRef(null);
    const lowFilterRef = useRef(null);
    const gainNodeRef = useRef(null);
    const wasPlayingBeforeDragRef = useRef(false);
    const isProcessingPlayPauseRef = useRef(false);
    const isActuallyPlayingRef = useRef(false);
    const lastLyricTime = useRef(0);
    const turntablePlatterRef = useRef(null);
    const animationFrameRef = useRef(null);
    const audioBufferReversedRef = useRef(null); // 反转的音频缓冲区（用于反向播放）
    const isReversedRef = useRef(false); // 当前是否反向播放
    const isDraggingRef = useRef(false); // 拖曳状态 ref（用于循环中访问）
    const currentTimeRef = useRef(0); // mirror of currentTime state for use in callbacks without deps churn
    
    // Scratch speed calculation refs
    const previousRotationRef = useRef(0); // 上一帧的角度（用于计算速度）
    const timestampPreviousRef = useRef(performance.now()); // 上一帧的时间戳
    const draggingSpeedsRef = useRef([]); // 速度历史（用于平滑）
    const currentPlaybackSpeedRef = useRef(1); // 当前播放速度

    // 计算当前 BPM
    const getCurrentBPM = useCallback(() => {
        const adjustment = (bpmValue - 50) / 50 * BPM_ADJUSTMENT_PERCENTAGE;
        return BASE_BPM * (1 + adjustment);
    }, [bpmValue, BASE_BPM, BPM_ADJUSTMENT_PERCENTAGE]);
    
    const currentBPM = useMemo(() => getCurrentBPM(), [getCurrentBPM]);

    // 格式化时间
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // 统一的时间更新函数，同时更新 state 和调用回调
    const updateCurrentTime = useCallback((newTime) => {
        currentTimeRef.current = newTime;
        setCurrentTime(newTime);
        if (onTimeUpdate) {
            onTimeUpdate(newTime);
        }
    }, [onTimeUpdate]);

    // 创建反转的音频缓冲区（用于反向播放）
    const createReversedAudioBuffer = useCallback((audioContext, audioBuffer) => {
        const numberOfChannels = audioBuffer.numberOfChannels;
        const length = audioBuffer.length;
        const sampleRate = audioBuffer.sampleRate;
        const reversedBuffer = audioContext.createBuffer(numberOfChannels, length, sampleRate);
        
        for (let channel = 0; channel < numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const reversedData = reversedBuffer.getChannelData(channel);
            // 反转数据
            for (let i = 0; i < length; i++) {
                reversedData[i] = originalData[length - 1 - i];
                
            }
        }
        
        return reversedBuffer;
    }, []);

    // 初始化音频
    useEffect(() => {
        if (!audioPath) return;

        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            return;
        }

        const initAudio = async () => {
            try {
                const fullPath = `${process.env.PUBLIC_URL || ''}${audioPath}`;
                const { audioContext, audioBuffer, highFilter, midFilter, lowFilter, gainNode } = 
                    await initializeAudio(fullPath, {
                        volume: 100,
                        eqValues: { high: 50, mid: 50, low: 50 }
                    });

                audioContextRef.current = audioContext;
                audioBufferRef.current = audioBuffer;
                highFilterRef.current = highFilter;
                midFilterRef.current = midFilter;
                lowFilterRef.current = lowFilter;
                gainNodeRef.current = gainNode;
                
                // 创建反转的音频缓冲区（用于反向播放）
                try {
                    const reversedBuffer = createReversedAudioBuffer(audioContext, audioBuffer);
                    audioBufferReversedRef.current = reversedBuffer;
                } catch (err) {
                    console.warn('Failed to create reversed audio buffer:', err);
                }
                
                setDuration(audioBuffer.duration);
            } catch (err) {
                console.error('Failed to initialize audio:', err);
            }
        };

        initAudio();

        return () => {
            if (sourceNodeRef.current) {
                try {
                    sourceNodeRef.current.stop();
                } catch (e) {
                    // 忽略已经停止的错误
                }
                sourceNodeRef.current = null;
            }
            highFilterRef.current = null;
            midFilterRef.current = null;
            lowFilterRef.current = null;
            gainNodeRef.current = null;
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createReversedAudioBuffer is stable
    }, [audioPath]);

    // 确保音频节点
    const ensureAudioNodes = useCallback(() => {
        const audioContext = audioContextRef.current;
        if (!audioContext) {
            return false;
        }

        const nodes = ensureAudioNodesUtil(
            audioContext,
            {
                highFilter: highFilterRef.current,
                midFilter: midFilterRef.current,
                lowFilter: lowFilterRef.current,
                gainNode: gainNodeRef.current
            },
            {
                volume,
                eqValues
            }
        );

        if (!nodes) {
            return false;
        }

        highFilterRef.current = nodes.highFilter;
        midFilterRef.current = nodes.midFilter;
        lowFilterRef.current = nodes.lowFilter;
        gainNodeRef.current = nodes.gainNode;

        return true;
    }, [volume, eqValues]);

    // 播放/暂停
    const handlePlayPause = async () => {
        // 防止快速点击导致的问题
        if (isProcessingPlayPauseRef.current) {
            return;
        }
        
        isProcessingPlayPauseRef.current = true;
        
        try {
            if (!audioContextRef.current || !audioBufferRef.current) {
                const errorMsg = !audioContextRef.current 
                    ? 'Audio context not initialized. Please refresh the page.' 
                    : 'Audio file not loaded. Please refresh the page.';
                console.error(errorMsg);
                isProcessingPlayPauseRef.current = false;
                return;
            }

            if (audioContextRef.current.state === 'suspended') {
                try {
                    await audioContextRef.current.resume();
                } catch (err) {
                    console.error('Failed to resume audio context:', err);
                    isProcessingPlayPauseRef.current = false;
                    return;
                }
            }

            const nodesReady = ensureAudioNodes();
            if (!nodesReady) {
                console.error('Failed to initialize audio nodes. Please refresh the page.');
                isProcessingPlayPauseRef.current = false;
                return;
            }

            // 使用 ref 来获取最新的播放状态，避免闭包问题
            // 检查实际的播放状态：ref 和 sourceNode 都存在
            const currentlyPlaying = isActuallyPlayingRef.current && sourceNodeRef.current !== null;

            if (currentlyPlaying) {
                // 暂停操作
                // 立即更新 ref，防止快速点击导致的问题
                isActuallyPlayingRef.current = false;
                
                // 在停止前，计算并保存当前的准确播放时间
                if (audioContextRef.current && sourceNodeRef.current && startTimeRef.current) {
                    const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
                    const pausedTime = Math.min(Math.max(0, elapsed), duration);
                    updateCurrentTime(pausedTime);
                    
                    // 根据暂停时间更新旋转角度，保持圈数一致
                    const pausedRotation = timeToRotation(pausedTime);
                    setRotation(pausedRotation);
                    rotationRef.current = pausedRotation;
                }
                
                if (sourceNodeRef.current) {
                    try {
                        sourceNodeRef.current.stop();
                    } catch (e) {
                        // 忽略已经停止的错误
                    }
                    sourceNodeRef.current = null;
                }
                
                setIsPlaying(false);
                if (onPlayingStateUpdate) {
                    onPlayingStateUpdate(false);
                }
        } else {
            // 确保之前的 sourceNode 已经停止
            if (sourceNodeRef.current) {
                try {
                    sourceNodeRef.current.stop();
                } catch (e) {
                    // 忽略已经停止的错误
                }
                sourceNodeRef.current = null;
            }
            
            // 播放
            const audioContext = audioContextRef.current;
            const highFilter = highFilterRef.current;
            
            const source = audioContext.createBufferSource();
            source.buffer = audioBufferRef.current;
            
            const currentBPM = getCurrentBPM();
            source.playbackRate.value = currentBPM / BASE_BPM;
            
            try {
                source.connect(highFilter);
            } catch (err) {
                console.error('Failed to connect audio nodes:', err);
                isProcessingPlayPauseRef.current = false;
                return;
            }
            
            sourceNodeRef.current = source;
            // 确保 startOffset 在有效范围内（>= 0 且 <= duration）
            const startOffset = Math.max(0, Math.min(duration, currentTime));
            startTimeRef.current = audioContext.currentTime - startOffset;
            source.start(0, startOffset);
            setIsPlaying(true);
            isActuallyPlayingRef.current = true;
            if (onPlayingStateUpdate) {
                onPlayingStateUpdate(true);
            }
            
            // 初始化 scratch 相关 refs
            timestampPreviousRef.current = performance.now();
            previousRotationRef.current = rotationRef.current || rotation;
            
            // 使用 requestAnimationFrame 替代 setInterval，性能更好，支持 scratch
            let lastUpdateTime = 0;
            const baseAnglePerMs = calculateBaseAnglePerMs();
            
            const updateProgress = () => {
                const now = performance.now();
                const timeDelta = now - timestampPreviousRef.current;
                const currentRotation = rotationRef.current || rotation;
                const angleDelta = currentRotation - previousRotationRef.current;
                
                // 计算播放速度（用于 scratch）
                const baseAngle = baseAnglePerMs * timeDelta;
                const playbackSpeed = timeDelta > 0 && baseAngle !== 0 ? (angleDelta / baseAngle || 0) : 1;
                const isReversed = angleDelta < 0;
                
                // 如果正在拖曳，平滑速度并实时更新播放速度
                if (isDraggingRef.current && wasPlayingBeforeDragRef.current && audioContextRef.current && sourceNodeRef.current) {
                    // 只有在角度有变化时才更新（避免停着不动时抖动）
                    if (Math.abs(angleDelta) > 0.01) {
                        
                        const smoothedSpeed = smoothSpeed(playbackSpeed);
                        // 拖曳时使用固定的 BASE_BPM 转换，不受 BPM 调整影响
                        const currentTime = rotationToTimeForDrag(currentRotation) % duration;
                        
                        // 实时更新播放速度
                        updatePlaybackSpeed(smoothedSpeed, isReversed, currentTime);
                        
                        // 更新显示（节流更新，避免频繁触发 onTimeUpdate）
                        const updateNow = Date.now();
                        if (updateNow - lastUpdateTime >= 50) {
                            updateCurrentTime(currentTime);
                            lastUpdateTime = updateNow;
                        }
                    }
                } else if (!isDraggingRef.current && audioContextRef.current && sourceNodeRef.current) {
                    // 不拖曳时，正常更新进度
                    const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
                    const newTime = Math.min(Math.max(0, elapsed), duration);
                    
                    // 直接更新 DOM，避免 React re-render
                    const newRotation = timeToRotation(newTime);
                    rotationRef.current = newRotation;
                    updatePlatterTransform(newRotation);
                    
                    // 节流 state 更新：每 100ms 更新一次，减少 re-render
                    const updateNow = Date.now();
                    if (updateNow - lastUpdateTime >= 100) {
                        updateCurrentTime(newTime);
                        lastUpdateTime = updateNow;
                    }
                    
                    if (newTime >= duration) {
                        setIsPlaying(false);
                        isActuallyPlayingRef.current = false;
                        updateCurrentTime(0);
                        setRotation(0);
                        rotationRef.current = 0;
                        if (onPlayingStateUpdate) {
                            onPlayingStateUpdate(false);
                        }
                        if (animationFrameRef.current) {
                            cancelAnimationFrame(animationFrameRef.current);
                            animationFrameRef.current = null;
                        }
                    }
                }
                
                // 更新上一帧的值（用于下次速度计算）
                previousRotationRef.current = currentRotation;
                timestampPreviousRef.current = now;
                
                // 继续循环
                if (audioContextRef.current && sourceNodeRef.current) {
                    animationFrameRef.current = requestAnimationFrame(updateProgress);
                } else if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
            };
            
            animationFrameRef.current = requestAnimationFrame(updateProgress);
            
            source.onended = () => {
                setIsPlaying(false);
                isActuallyPlayingRef.current = false;
                setCurrentTime(0);
                if (onPlayingStateUpdate) {
                    onPlayingStateUpdate(false);
                }
                sourceNodeRef.current = null;
            };
        }
        } finally {
            // 使用 setTimeout 确保状态更新完成后再允许下一次点击
            // 增加延迟时间，确保所有状态更新完成
            setTimeout(() => {
                isProcessingPlayPauseRef.current = false;
            }, 200);
        }
    };

    // 更新 BPM 时调整播放速度
    useEffect(() => {
        if (onBPMUpdate) {
            onBPMUpdate(currentBPM);
        }
        if (sourceNodeRef.current && isPlaying) {
            sourceNodeRef.current.playbackRate.value = currentBPM / BASE_BPM;
        }
    }, [currentBPM, isPlaying, BASE_BPM, onBPMUpdate]);

    // 更新音量
    useEffect(() => {
        if (!gainNodeRef.current) return;
        
        const audioContext = audioContextRef.current;
        if (!audioContext || gainNodeRef.current.context !== audioContext) {
            return;
        }
        
        updateVolume(gainNodeRef.current, volume);
    }, [volume, eqValues]);

    // 更新 EQ
    useEffect(() => {
        if (!highFilterRef.current || !midFilterRef.current || !lowFilterRef.current) return;

        const audioContext = audioContextRef.current;
        if (!audioContext || 
            highFilterRef.current.context !== audioContext ||
            midFilterRef.current.context !== audioContext ||
            lowFilterRef.current.context !== audioContext) {
            return;
        }

        updateEQGain(highFilterRef.current, eqValues.high);
        updateEQGain(midFilterRef.current, eqValues.mid);
        updateEQGain(lowFilterRef.current, eqValues.low);
    }, [eqValues]);

    // 初始化 EQ 旋转角度
    useEffect(() => {
        const initialRotation = 0;
        setEqRotation({
            high: initialRotation,
            mid: initialRotation,
            low: initialRotation
        });
    }, []);

    // BPM 滑块控制
    const handleBPMSliderMouseDown = (e) => {
        e.preventDefault();
        setIsDraggingBPM(true);
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
        setBpmValue(percentage);
    };

    const handleBPMSliderMouseMove = useCallback((e) => {
        if (!isDraggingBPM) return;
        const slider = document.querySelector('[data-bpm-slider]');
        if (slider) {
            const rect = slider.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
            setBpmValue(percentage);
        }
    }, [isDraggingBPM]);

    const handleBPMSliderMouseUp = useCallback(() => {
        setIsDraggingBPM(false);
    }, []);

    useEffect(() => {
        if (isDraggingBPM) {
            document.addEventListener('mousemove', handleBPMSliderMouseMove);
            document.addEventListener('mouseup', handleBPMSliderMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleBPMSliderMouseMove);
            document.removeEventListener('mouseup', handleBPMSliderMouseUp);
        };
    }, [isDraggingBPM, handleBPMSliderMouseMove, handleBPMSliderMouseUp]);

    // 音量控制
    const handleVolumeMouseDown = (e) => {
        e.preventDefault();
        setIsDraggingVolume(true);
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
        setVolume(percentage);
    };

    const handleVolumeMouseMove = useCallback((e) => {
        if (!isDraggingVolume) return;
        const slider = document.querySelector('[data-volume-slider]');
        if (slider) {
            const rect = slider.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
            setVolume(percentage);
        }
    }, [isDraggingVolume]);

    const handleVolumeMouseUp = useCallback(() => {
        setIsDraggingVolume(false);
    }, []);

    useEffect(() => {
        if (isDraggingVolume) {
            document.addEventListener('mousemove', handleVolumeMouseMove);
            document.addEventListener('mouseup', handleVolumeMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleVolumeMouseMove);
            document.removeEventListener('mouseup', handleVolumeMouseUp);
        };
    }, [isDraggingVolume, handleVolumeMouseMove, handleVolumeMouseUp]);

    // 停止当前 source
    const stopSource = useCallback(() => {
        if (sourceNodeRef.current) {
            sourceNodeRef.current.onended = null;
            try {
                sourceNodeRef.current.stop();
            } catch (e) {
                // 忽略错误
            }
            sourceNodeRef.current = null;
        }
    }, []);

    // 停止动画循环
    const stopAnimationLoop = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    // 通用的 onended 回调
    const createOnEndedCallback = useCallback(() => {
        return () => {
            if (!sourceNodeRef.current) return;
            setIsPlaying(false);
            updateCurrentTime(0);
            setRotation(0);
            rotationRef.current = 0;
            stopAnimationLoop();
            sourceNodeRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- updateCurrentTime from ref
    }, [stopAnimationLoop]);

    // 创建并启动 audio source
    const createAndStartSource = useCallback((offset, playbackRateValue, useReversed = false) => {
        if (!audioContextRef.current || !audioBufferRef.current) return false;

        const audioContext = audioContextRef.current;
        const buffer = useReversed && audioBufferReversedRef.current
            ? audioBufferReversedRef.current
            : audioBufferRef.current;
        const highFilter = highFilterRef.current;

        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.playbackRate.value = playbackRateValue;
        source.connect(highFilter);
        source.onended = createOnEndedCallback();

        sourceNodeRef.current = source;
        startTimeRef.current = audioContext.currentTime - offset / playbackRateValue;
        source.start(0, offset);

        return true;
    }, [createOnEndedCallback]);

    // 进度控制 — 共用 seek helper
    const seekAudioToTime = useCallback((newTime) => {
        if (!isActuallyPlayingRef.current || !sourceNodeRef.current || !audioContextRef.current) return;
        const nodesReady = ensureAudioNodes();
        if (!nodesReady) return;
        const safeNewTime = Math.max(0, Math.min(duration, newTime));
        const currentBPM = getCurrentBPM();
        stopSource();
        createAndStartSource(safeNewTime, currentBPM / BASE_BPM);
    }, [duration, ensureAudioNodes, stopSource, createAndStartSource, getCurrentBPM, BASE_BPM]);

    // 从事件（mouse 或 touch）中读取相对于 progress bar 的 x 百分比
    const getProgressPercentage = (clientX, element) => {
        const rect = element.getBoundingClientRect();
        const x = clientX - rect.left;
        return Math.max(0, Math.min(100, (x / rect.width) * 100));
    };

    const handleProgressMouseDown = (e) => {
        e.preventDefault();
        setIsDraggingProgress(true);
        const percentage = getProgressPercentage(e.clientX, e.currentTarget);
        const newTime = (percentage / 100) * duration;
        updateCurrentTime(newTime);
        seekAudioToTime(newTime);
    };

    const handleProgressTouchStart = (e) => {
        e.preventDefault();
        setIsDraggingProgress(true);
        const touch = e.touches[0];
        const percentage = getProgressPercentage(touch.clientX, e.currentTarget);
        const newTime = (percentage / 100) * duration;
        updateCurrentTime(newTime);
        seekAudioToTime(newTime);
    };

    const handleProgressMouseMove = useCallback((e) => {
        if (!isDraggingProgress) return;
        const progressBar = document.querySelector('[data-progress-bar]');
        if (progressBar) {
            const percentage = getProgressPercentage(e.clientX, progressBar);
            const newTime = (percentage / 100) * duration;
            updateCurrentTime(newTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- updateCurrentTime from ref
    }, [isDraggingProgress, duration]);

    const handleProgressTouchMove = useCallback((e) => {
        if (!isDraggingProgress) return;
        e.preventDefault();
        const progressBar = document.querySelector('[data-progress-bar]');
        if (progressBar) {
            const touch = e.touches[0];
            const percentage = getProgressPercentage(touch.clientX, progressBar);
            const newTime = (percentage / 100) * duration;
            updateCurrentTime(newTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- updateCurrentTime from ref
    }, [isDraggingProgress, duration]);

    const handleProgressMouseUp = useCallback((e) => {
        if (!isDraggingProgress) return;
        setIsDraggingProgress(false);
        // 拖曳結束時 seek 到最終位置
        const progressBar = document.querySelector('[data-progress-bar]');
        if (progressBar && e && e.clientX !== undefined) {
            const percentage = getProgressPercentage(e.clientX, progressBar);
            const newTime = (percentage / 100) * duration;
            updateCurrentTime(newTime);
            seekAudioToTime(newTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- updateCurrentTime/seekAudioToTime from refs
    }, [isDraggingProgress, duration, seekAudioToTime]);

    const handleProgressTouchEnd = useCallback((e) => {
        if (!isDraggingProgress) return;
        setIsDraggingProgress(false);
        // 拖曳結束時 seek 到最終位置
        const progressBar = document.querySelector('[data-progress-bar]');
        if (progressBar && e.changedTouches && e.changedTouches.length > 0) {
            const touch = e.changedTouches[0];
            const percentage = getProgressPercentage(touch.clientX, progressBar);
            const newTime = (percentage / 100) * duration;
            updateCurrentTime(newTime);
            seekAudioToTime(newTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- updateCurrentTime/seekAudioToTime from refs
    }, [isDraggingProgress, duration, seekAudioToTime]);

    useEffect(() => {
        if (isDraggingProgress) {
            document.addEventListener('mousemove', handleProgressMouseMove);
            document.addEventListener('mouseup', handleProgressMouseUp);
            document.addEventListener('touchmove', handleProgressTouchMove, { passive: false });
            document.addEventListener('touchend', handleProgressTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleProgressMouseMove);
            document.removeEventListener('mouseup', handleProgressMouseUp);
            document.removeEventListener('touchmove', handleProgressTouchMove);
            document.removeEventListener('touchend', handleProgressTouchEnd);
        };
    }, [isDraggingProgress, handleProgressMouseMove, handleProgressMouseUp, handleProgressTouchMove, handleProgressTouchEnd]);

    // EQ 控制
    const resetEQ = (type) => {
        setEqValues(prev => ({
            ...prev,
            [type]: 50
        }));
        setEqRotation(prev => ({
            ...prev,
            [type]: 0
        }));
    };

    // 获取事件坐标（支持鼠标和触摸）
    const getEventCoordinates = (e) => {
        // Pointer events 统一处理鼠标和触摸，直接使用 clientX/clientY
        if (e.pointerType) {
            return { clientX: e.clientX, clientY: e.clientY };
        }
        // 兼容旧的触摸事件
        if (e.touches && e.touches.length > 0) {
            return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
        }
        return { clientX: e.clientX, clientY: e.clientY };
    };

    const handleEQMouseDown = (type, e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const coords = getEventCoordinates(e);
        setEqStartY(prev => ({ ...prev, [type]: coords.clientY }));
        setEqStartX(prev => ({ ...prev, [type]: coords.clientX }));
        setEqStartRotation(prev => ({ ...prev, [type]: eqRotation[type] }));
        setEqHasMoved(prev => ({ ...prev, [type]: false }));
        setIsDraggingEQ(prev => ({ ...prev, [type]: true }));
    };

    const handleEQMouseMove = (type, e) => {
        if (!isDraggingEQ[type] || eqStartY[type] === null || eqStartX[type] === null) return;
        
        const coords = getEventCoordinates(e);
        const deltaY = eqStartY[type] - coords.clientY;
        const deltaX = coords.clientX - eqStartX[type];
        
        if (Math.abs(deltaY) > 2 || Math.abs(deltaX) > 2) {
            setEqHasMoved(prev => ({ ...prev, [type]: true }));
        }
        
        const sensitivity = 1.5;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        let direction = 0;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
            direction = deltaY > 0 ? 1 : -1;
        } else {
            direction = deltaX > 0 ? 1 : -1;
        }
        
        const deltaAngle = distance * direction * sensitivity;
        const newRotation = eqStartRotation[type] + deltaAngle;
        const clampedRotation = Math.max(-150, Math.min(150, newRotation));
        const value = 50 + (clampedRotation / 150) * 50;
        
        setEqRotation(prev => ({ ...prev, [type]: clampedRotation }));
        setEqValues(prev => ({
            ...prev,
            [type]: Math.max(0, Math.min(100, value))
        }));
        
        setEqStartY(prev => ({ ...prev, [type]: coords.clientY }));
        setEqStartX(prev => ({ ...prev, [type]: coords.clientX }));
        setEqStartRotation(prev => ({ ...prev, [type]: clampedRotation }));
    };

    const handleEQMouseUp = (type, e) => {
        const wasDragging = isDraggingEQ[type];
        const hasMoved = eqHasMoved[type];
        
        setIsDraggingEQ(prev => ({ ...prev, [type]: false }));
        
        if (wasDragging && !hasMoved) {
            resetEQ(type);
        }
        
        setEqStartY(prev => ({ ...prev, [type]: null }));
        setEqStartX(prev => ({ ...prev, [type]: null }));
        setEqHasMoved(prev => ({ ...prev, [type]: false }));
    };

    const handleEQClick = (type, e) => {
        e.stopPropagation();
        if (!isDraggingEQ[type]) {
            resetEQ(type);
        }
    };

    // 全局鼠标和触摸事件处理 EQ 拖动
    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            ['high', 'mid', 'low'].forEach(type => {
                if (!isDraggingEQ[type] || eqStartY[type] === null || eqStartX[type] === null) return;
                handleEQMouseMove(type, e);
            });
        };

        const handleGlobalMouseUp = (e) => {
            ['high', 'mid', 'low'].forEach(type => {
                if (!isDraggingEQ[type]) return;
                handleEQMouseUp(type, e);
            });
        };

        const handleGlobalTouchMove = (e) => {
            e.preventDefault();
            ['high', 'mid', 'low'].forEach(type => {
                if (!isDraggingEQ[type] || eqStartY[type] === null || eqStartX[type] === null) return;
                handleEQMouseMove(type, e);
            });
        };

        const handleGlobalTouchEnd = (e) => {
            ['high', 'mid', 'low'].forEach(type => {
                if (!isDraggingEQ[type]) return;
                handleEQMouseUp(type, e);
            });
        };

        if (isDraggingEQ.high || isDraggingEQ.mid || isDraggingEQ.low) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
            document.addEventListener('touchend', handleGlobalTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('touchmove', handleGlobalTouchMove);
            document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDraggingEQ, eqStartY, eqStartX, eqStartRotation, eqRotation, eqHasMoved]);

    // CUE 控制
    const handleCueClick = () => {
        setCueActive(!cueActive);
        setTimeout(() => setCueActive(false), 200);
        
        // 向上回传给 MusicPage 处理滚动
        if (onCueClick) {
            onCueClick();
        }
    };

    // 歌词气泡生成（当转盘被拖动时）
    // 如果有 lrcData（时间戳歌词），优先显示当前时间对应的歌词行；
    // 否则 fallback 到从 lyrics 数组随机取一句。
    const spawnLyrics = useCallback(() => {
        if (lyrics.length === 0 && lrcData.length === 0) return;

        const now = Date.now();
        if (now - lastLyricTime.current < 800) return;
        lastLyricTime.current = now;

        const shouldSpawn = Math.random() > 0.3;
        if (!shouldSpawn) return;

        let lyricText;
        if (lrcData.length > 0) {
            // Time-based: show lyric corresponding to current playback position
            const idx = findCurrentLyricIndex(lrcData, currentTimeRef.current);
            if (idx >= 0 && lrcData[idx].text) {
                lyricText = lrcData[idx].text;
            } else {
                // Before first lyric or no match — pick a random lrcData entry
                lyricText = lrcData[Math.floor(Math.random() * lrcData.length)].text;
            }
        } else {
            lyricText = lyrics[Math.floor(Math.random() * lyrics.length)];
        }

        const angle = (Math.random() * 360) * (Math.PI / 180);
        const distance = 100 + Math.random() * 100;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;
        const rotate = (Math.random() * 20 - 10);

        const selectedLyric = {
            id: Date.now() + Math.random(),
            text: lyricText,
            tx: `${tx}px`,
            ty: `${ty}px`,
            rotate: `${rotate}deg`
        };

        setLyricBubbles(prev => [...prev, selectedLyric]);

        setTimeout(() => {
            setLyricBubbles(prev => prev.filter(bubble => bubble.id !== selectedLyric.id));
        }, 2000);
    }, [lyrics, lrcData]);

    // 转盘控制
    const getAngle = (centerX, centerY, x, y) => {
        const dx = x - centerX;
        const dy = y - centerY;
        return Math.atan2(dy, dx) * (180 / Math.PI);
    };

    // 计算一圈对应的时间
    const getTurntableAnimationDuration = useCallback(() => (60 / baseBPM) * 20, [baseBPM]);

    // 根据播放时间计算旋转角度（保持圈数一致）
    const timeToRotation = (time) => {
        if (!duration || duration === 0) return 0;
        const currentBPMValue = getCurrentBPM();
        const turntableAnimationDuration = (60 / currentBPMValue) * 20; // 一圈对应的时间
        // 计算总圈数：播放时间 / 一圈时间
        const totalRotations = time / turntableAnimationDuration;
        // 应用旋转速率系数：系数越大，旋转越快
        // 转换为角度（保持完整的圈数信息）
        return totalRotations * 360;
    };

    // 根据旋转角度计算播放时间（保持圈数一致）
    const rotationToTime = (angle) => {
        if (!duration || duration === 0) return 0;
        const rotations = angle / 360;
        const currentBPMValue = getCurrentBPM();
        const turntableAnimationDuration = (60 / currentBPMValue) * 20; // 一圈对应的时间
    
        // 转换为时间（保持完整的圈数信息）
        let time = rotations * turntableAnimationDuration;
        // 处理负数：如果时间为负，将其转换为正数（通过加上 duration 的倍数）
        while (time < 0) {
            time += duration;
        }
        // 如果时间超过音频时长，取模以循环
        // 但保持圈数信息，所以使用完整的 time，只在显示时取模
        return time;
    };

    // 拖曳时使用的旋转角度到时间转换（使用固定的 BASE_BPM，不受 BPM 调整影响）
    const rotationToTimeForDrag = useCallback((angle) => {
        if (!duration || duration === 0) return 0;
        const turntableAnimationDuration = getTurntableAnimationDuration();
        const rotations = angle / 360;
        let time = rotations * turntableAnimationDuration;
        while (time < 0) time += duration; // 处理负数
        return time;
    }, [duration, getTurntableAnimationDuration]);

    // 计算基准角度变化率（每毫秒的角度变化，度）
    // 拖曳时使用固定的 BASE_BPM，不受 BPM 调整影响
    const calculateBaseAnglePerMs = useCallback(() => {
        const turntableAnimationDuration = getTurntableAnimationDuration();
        return 360 / (turntableAnimationDuration * 1000);
    }, [getTurntableAnimationDuration]);

    // 速度平滑处理（移动平均）
    const smoothSpeed = useCallback((speed) => {
        const speeds = draggingSpeedsRef.current;
        speeds.push(speed);
        if (speeds.length > 10) speeds.shift();
        const avg = speeds.reduce((sum, s) => sum + s, 0) / speeds.length;
        return Math.max(-4, Math.min(4, avg));
    }, []);

    // 更新音频播放速度
    const updatePlaybackSpeed = useCallback((speed, isReversed, currentTime) => {
        if (!sourceNodeRef.current || !audioContextRef.current) return;
        
        const audioContext = audioContextRef.current;
        const speedAbs = Math.max(0.001, Math.abs(speed));
        
        // 如果方向改变，需要切换 buffer 并重新创建 source
        if (isReversed !== isReversedRef.current) {
            stopSource();
            
            if (!ensureAudioNodes()) {
                console.error('Failed to initialize audio nodes');
                return;
            }
            
            const offset = isReversed ? duration - currentTime : currentTime;
            createAndStartSource(offset, speedAbs, isReversed);
            isReversedRef.current = isReversed;
        } else {
            // 只更新播放速度（拖曳时使用原始速度，不受 BPM 影响）
            const now = audioContext.currentTime;
            sourceNodeRef.current.playbackRate.cancelScheduledValues(now);
            sourceNodeRef.current.playbackRate.linearRampToValueAtTime(speedAbs, now);
        }
        
        currentPlaybackSpeedRef.current = speed;
    }, [duration, ensureAudioNodes, stopSource, createAndStartSource]);
    

    // 直接更新 DOM transform，避免 React re-render（性能优化）
    const updatePlatterTransform = useCallback((rotationValue) => {
        if (!turntablePlatterRef.current) return;
        
        const layout = COMPONENT_LAYOUTS[size]?.platter || {};
        let transformParts = [];
        const needsCentering = !layout.top && !layout.bottom && !layout.left && !layout.right;
        if (needsCentering) {
            transformParts.push('translate(-50%, -50%)');
        }
        if (layout.transform) {
            transformParts.push(layout.transform);
        }
        transformParts.push(`rotate(${rotationValue}deg)`);
        turntablePlatterRef.current.style.transform = transformParts.join(' ');
    }, [size]);

    const handleTurntablePointerDown = (e) => {
        e.preventDefault();
        // 设置 pointer capture 以确保后续事件都能捕获到
        if (e.currentTarget.setPointerCapture) {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        const coords = getEventCoordinates(e);
        // 使用 ref 获取元素，确保获取正确的边界框
        const element = turntablePlatterRef.current || e.currentTarget;
        const rect = element.getBoundingClientRect();
        // 计算并保存中心点（在拖曳开始时计算一次，避免旋转后 getBoundingClientRect 不准确）
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        centerPointRef.current = { x: centerX, y: centerY };
        const angle = getAngle(centerX, centerY, coords.clientX, coords.clientY);
        
        // 如果正在播放，从 DOM 获取当前的实际角度
        let currentRotation = rotationRef.current || rotation;
        
        setIsDragging(true);
        isDraggingRef.current = true;
        rotationRef.current = currentRotation;
        lastMouseAngleRef.current = angle;
        cumulativeAngleRef.current = currentRotation;
        
        // 初始化 scratch 相关 refs
        previousRotationRef.current = currentRotation;
        timestampPreviousRef.current = performance.now();
        draggingSpeedsRef.current = [];
        currentPlaybackSpeedRef.current = 1;
        
        // 记住拖曳前是否在播放
        wasPlayingBeforeDragRef.current = isPlaying && sourceNodeRef.current !== null;
        
        // 注意：不再停止播放，循环会持续运行并实时更新播放速度
    };


    // 计算角度差，处理跨越 0/360 度边界的情况
    // 使用累积角度来跟踪完整的旋转，避免丢失圈数
    const getAngleDelta = (currentAngle, lastAngle, currentCumulativeAngle) => {
        let delta = currentAngle - lastAngle;
        // 如果角度差超过 180 度，说明跨越了边界，需要调整
        if (delta > 180) {
            delta -= 360;
        } else if (delta < -180) {
            delta += 360;
        }
        // 更新累积角度
        const newCumulativeAngle = currentCumulativeAngle + delta;
        return { delta, cumulativeAngle: newCumulativeAngle };
    };

    const handleTurntableMouseMove = (e) => {
        if (!isDragging) return;
        
        const coords = getEventCoordinates(e);
        // 使用保存的中心点，避免旋转后 getBoundingClientRect 不准确
        const centerX = centerPointRef.current.x;
        const centerY = centerPointRef.current.y;
        const currentAngle = getAngle(centerX, centerY, coords.clientX, coords.clientY);
        
        // 计算角度增量，处理跨越边界的情况
        const { delta, cumulativeAngle } = getAngleDelta(currentAngle, lastMouseAngleRef.current, cumulativeAngleRef.current);
        const newRotation = cumulativeAngle;
        
        // 只有在角度有实际变化时才更新（避免停着不动时抖动）
        if (Math.abs(delta) > 0.01 || Math.abs(newRotation - rotationRef.current) > 0.01) {
            setRotation(newRotation);
            rotationRef.current = newRotation;
            lastMouseAngleRef.current = currentAngle;
            cumulativeAngleRef.current = cumulativeAngle;
            
            // 更新拖曳调试信息
            setDragDebugInfo({
                centerX: centerX.toFixed(1),
                centerY: centerY.toFixed(1),
                mouseX: coords.clientX.toFixed(1),
                mouseY: coords.clientY.toFixed(1),
                currentAngle: currentAngle.toFixed(1),
                lastMouseAngle: lastMouseAngleRef.current.toFixed(1),
                cumulativeAngle: cumulativeAngle.toFixed(1),
                angleDelta: delta.toFixed(1)
            });
            
            // 更新显示时间（使用拖曳专用的转换函数，不受 BPM 影响）
            // 节流更新，避免频繁触发 onTimeUpdate 导致波形抖动
            const now = Date.now();
            if (now - lastDragUpdateTimeRef.current >= 50) {
                const newTime = rotationToTimeForDrag(newRotation);
                const clampedTime = Math.max(0, Math.min(duration, newTime % duration));
                updateCurrentTime(clampedTime);
                lastDragUpdateTimeRef.current = now;
            }
            
            // 拖动时生成歌词气泡
            spawnLyrics();
        }
    };

    const handleTurntableMouseUp = useCallback(async () => {
        if (!isDragging) return;
        
        setIsDragging(false);
        isDraggingRef.current = false;
        
        // 重置速度
        draggingSpeedsRef.current = [];
        currentPlaybackSpeedRef.current = 1;
        
        const finalRotation = rotationRef.current;
        // 拖曳结束时使用固定的 BASE_BPM 转换，不受 BPM 调整影响
        const finalTime = rotationToTimeForDrag(finalRotation);
        const safeStartTime = Math.max(0, Math.min(duration, finalTime % duration));
        
        // 如果拖曳前正在播放，重置播放速度到正常速度并继续播放
        if (wasPlayingBeforeDragRef.current && sourceNodeRef.current && audioContextRef.current) {
            const currentBPMValue = getCurrentBPM();
            const basePlaybackRate = currentBPMValue / BASE_BPM;
            const now = audioContextRef.current.currentTime;
            sourceNodeRef.current.playbackRate.cancelScheduledValues(now);
            sourceNodeRef.current.playbackRate.linearRampToValueAtTime(basePlaybackRate, now);
            
            // 从新位置继续播放（重新创建 source 以确保位置准确）
            stopSource();
            
            if (!ensureAudioNodes()) {
                console.error('Failed to initialize audio nodes');
                return;
            }
            
            if (audioContextRef.current.state === 'suspended') {
                try {
                    await audioContextRef.current.resume();
                } catch (err) {
                    console.error('Failed to resume audio context:', err);
                    return;
                }
            }
            
            const audioContext = audioContextRef.current;
            const highFilter = highFilterRef.current;
            const source = audioContext.createBufferSource();
            source.buffer = audioBufferRef.current;
            source.playbackRate.value = basePlaybackRate;
            source.connect(highFilter);
            source.onended = createOnEndedCallback();
            
            sourceNodeRef.current = source;
            startTimeRef.current = audioContext.currentTime - safeStartTime;
            source.start(0, safeStartTime);
            setIsPlaying(true);
            isActuallyPlayingRef.current = true;
            isReversedRef.current = false;
            
            if (onPlayingStateUpdate) {
                onPlayingStateUpdate(true);
            }
            
            // 重新初始化 scratch refs
            timestampPreviousRef.current = performance.now();
            previousRotationRef.current = finalRotation;
        }
        
        wasPlayingBeforeDragRef.current = false;
    }, [isDragging, duration, rotationToTimeForDrag, getCurrentBPM, BASE_BPM, stopSource, ensureAudioNodes, createOnEndedCallback, onPlayingStateUpdate]);

    // 同步 rotationRef 和 rotation state
    useEffect(() => {
        rotationRef.current = rotation;
    }, [rotation]);

    // 处理全局拖曳事件
    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (e) => {
            if (turntablePlatterRef.current) {
                handleTurntableMouseMove(e);
            }
        };

        const handlePointerUp = async (e) => {
            if (e.target && e.target.releasePointerCapture) {
                e.target.releasePointerCapture(e.pointerId);
            }
            await handleTurntableMouseUp();
        };

        document.body.addEventListener('pointermove', handlePointerMove);
        document.body.addEventListener('pointerup', handlePointerUp);

        return () => {
            document.body.removeEventListener('pointermove', handlePointerMove);
            document.body.removeEventListener('pointerup', handlePointerUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- handleTurntableMouseMove from ref
    }, [isDragging, handleTurntableMouseUp, duration, rotationToTimeForDrag]);

    // Pointer events 统一处理鼠标和触摸，不需要单独的 touchstart 处理


    const progressPercent = duration ? (currentTime / duration) * 100 : 0;

    // 获取当前 size 的布局配置
    const currentLayout = COMPONENT_LAYOUTS[size] || COMPONENT_LAYOUTS.large;

    // 计算调试信息
    const currentBPMValue = getCurrentBPM();
    const turntableAnimationDuration = getTurntableAnimationDuration(); // 一圈对应的时间
    
    // 
    const displayAngle = rotationRef.current; // 显示角度（已应用系数）
    const actualRotations = rotation / 360; // 实际物理圈数
    const rotationTime = rotationToTime(displayAngle); // 从角度计算的时间
    const trackOffset = currentTime; // Track offset 就是当前播放时间

    return (
        <>
            <DebugPanelButton onClick={() => setIsDebugPanelOpen(!isDebugPanelOpen)}>
                🔧
            </DebugPanelButton>
            <DebugPanel isOpen={isDebugPanelOpen}>
                <div><strong>播放秒數:</strong> {currentTime.toFixed(2)}秒</div>
                <div><strong>顯示角度:</strong> {displayAngle.toFixed(1)}°</div>
                <div><strong>實際圈數:</strong> {actualRotations.toFixed(3)}</div>
                <div><strong>角度對應時間:</strong> {rotationTime.toFixed(2)}秒</div>
                <div><strong>曲目偏移:</strong> {trackOffset.toFixed(2)}秒</div>
                <div><strong>BPM:</strong> {currentBPMValue.toFixed(1)}</div>
                <div><strong>一圈時長:</strong> {turntableAnimationDuration.toFixed(2)}秒</div>
                <div><strong>播放狀態:</strong> {isPlaying ? '播放中' : '暫停'}</div>
                <div><strong>拖曳狀態:</strong> {isDragging ? '拖曳中' : '未拖曳'}</div>
                {isDragging && (
                    <>
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                            <div><strong>中心點 X:</strong> {dragDebugInfo.centerX}</div>
                            <div><strong>中心點 Y:</strong> {dragDebugInfo.centerY}</div>
                            <div><strong>滑鼠 X:</strong> {dragDebugInfo.mouseX}</div>
                            <div><strong>滑鼠 Y:</strong> {dragDebugInfo.mouseY}</div>
                            <div><strong>當前角度:</strong> {dragDebugInfo.currentAngle}°</div>
                            <div><strong>上次角度:</strong> {dragDebugInfo.lastMouseAngle}°</div>
                            <div><strong>角度增量:</strong> {dragDebugInfo.angleDelta}°</div>
                            <div><strong>累積角度:</strong> {dragDebugInfo.cumulativeAngle}°</div>
                        </div>
                    </>
                )}
            </DebugPanel>
            <TurntableContainer>
            {lyricBubbles.map(bubble => (
                <LyricBubble
                    key={bubble.id}
                    tx={bubble.tx}
                    ty={bubble.ty}
                    rotate={bubble.rotate}
                >
                    {bubble.text}
                </LyricBubble>
            ))}
            <TurntableBase 
                size={size}
                baseWidth={BASE_WIDTH[size] || BASE_WIDTH.large}
                baseHeight={BASE_HEIGHT[size] || BASE_HEIGHT.large}
            >
                <TurntablePlatterContainer size={size}>
                    <TurntablePlatter
                        ref={turntablePlatterRef}
                        size={PLATTER_SIZES[size] || PLATTER_SIZES.large}
                        rotation={rotation}
                        data-turntable-platter
                        layout={currentLayout.platter}
                        onPointerDown={handleTurntablePointerDown}
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    >
                        <RecordImage />
                        <RecordGrooves />
                        <CenterSpindle size={currentLayout.centerHole?.size || 50}>
                            <CenterDot size={currentLayout.centerHole?.dotSize || 12} />
                        </CenterSpindle>
                    </TurntablePlatter>
                </TurntablePlatterContainer>
                <ControlsSection layout={currentLayout.controlsSection}>
                    <EQControlsContainer
                        layout={currentLayout.eqControlsContainer}
                    >
                        <EQControls
                            eqRotation={eqRotation}
                            onEQMouseDown={handleEQMouseDown}
                            onEQClick={handleEQClick}
                            knobConfig={currentLayout.eqKnob}
                        />
                    </EQControlsContainer>
                </ControlsSection>

                <VolumeSection layout={currentLayout.volumeSliderContainer}>
                    <VolumeSlider 
                        data-volume-slider
                        onMouseDown={handleVolumeMouseDown}
                    >
                        <VolumeLines>
                            {Array.from({ length: 10 }, (_, i) => {
                                const lineIndex = 9 - i;
                                const maxWidth = 30;
                                const minWidth = maxWidth / 2;
                                const lineWidth = minWidth + (lineIndex / 9) * (maxWidth - minWidth);
                                
                                return (
                                    <VolumeLine
                                        key={i}
                                        width={lineWidth}
                                    />
                                );
                            })}
                        </VolumeLines>
                        <VolumeTrack />
                        <VolumeHandle value={volume} />
                    </VolumeSlider>
                </VolumeSection>

                <BPMSliderContainer layout={currentLayout.bpmSliderContainer}>
                    <BPMValue>{Math.round(currentBPM)}</BPMValue>
                    <div style={{ position: 'relative', width: '100%', height: '100%', marginTop: '8px' }}>
                        <BPMMarkers>
                            <BPMMarker position="top">-</BPMMarker>
                            <BPMMarker position="middle">0</BPMMarker>
                            <BPMMarker position="bottom">+</BPMMarker>
                        </BPMMarkers>
                        <BPMSlider 
                            data-bpm-slider
                            onMouseDown={handleBPMSliderMouseDown}
                            style={{ position: 'absolute', right: 0, top: 0 }}
                        >
                            <BPMSliderTrack value={bpmValue} />
                            <BPMSliderHandle value={bpmValue} />
                        </BPMSlider>
                    </div>
                </BPMSliderContainer>

                <CueButton 
                    layout={currentLayout.cueButton}
                    onClick={handleCueClick}
                    style={{
                        background: cueActive 
                            ? 'radial-gradient(circle at 30% 30%, #ff4444 0%, #cc0000 100%)'
                            : 'radial-gradient(circle at 30% 30%, #3a3a3a 0%, #1a1a1a 100%)',
                        boxShadow: cueActive
                            ? '0 0 20px rgba(255, 68, 68, 0.6), inset 0 2px 5px rgba(0, 0, 0, 0.5)'
                            : 'inset 0 2px 5px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    CUE
                </CueButton>

                <CueKeysContainer layout={currentLayout.cueKeys}>
                    <CueKey
                        layout={currentLayout.cueKeys}
                        borderColor="#1DB954"
                        hoverColor="rgba(29, 185, 84, 0.15)"
                        activeColor="rgba(29, 185, 84, 0.25)"
                        onClick={() => onScrollToSection && onScrollToSection('spotify-section')}
                        title="Spotify"
                    />
                    <CueKey
                        layout={currentLayout.cueKeys}
                        borderColor="#ff5500"
                        hoverColor="rgba(255, 85, 0, 0.15)"
                        activeColor="rgba(255, 85, 0, 0.25)"
                        onClick={() => onScrollToSection && onScrollToSection('soundcloud-section')}
                        title="SoundCloud"
                    />
                    <CueKey
                        layout={currentLayout.cueKeys}
                        borderColor="#ffd700"
                        hoverColor="rgba(255, 215, 0, 0.15)"
                        activeColor="rgba(255, 215, 0, 0.25)"
                        onClick={() => onScrollToSection && onScrollToSection('lyrics-section')}
                        title="Lyrics"
                    />
                    <CueKey layout={currentLayout.cueKeys} />
                </CueKeysContainer>

                <PlayPauseButton 
                    layout={currentLayout.playPause}
                    isPlaying={isPlaying}
                    onClick={handlePlayPause}
                    title={isPlaying ? t('music.pause') : t('music.play')}
                >
                    {isPlaying ? '⏸' : '▶'}
                </PlayPauseButton>

                <ProgressSection layout={currentLayout.progressSection}>
                    <ProgressBar
                        data-progress-bar
                        onMouseDown={handleProgressMouseDown}
                        onTouchStart={handleProgressTouchStart}
                    >
                        <ProgressFill progress={progressPercent} />
                    </ProgressBar>
                    <TimeDisplay>
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </TimeDisplay>
                </ProgressSection>
            </TurntableBase>
        </TurntableContainer>
        </>
    );
};

export default DJController;
